// CSC Load-Profile chart — renders analyzeLoadProfile()/buildSyntheticAnalysis()
// output in the HTML-simulator style the user prefers (2026-07-24): header, four
// stat cards, Daily | Hourly | Monthly toggle, and a stacked bar chart (day-import
// blue · night-import purple · solar orange · export green). The stat cards recompute
// for the selected view — a typical day / hour / month. App styling (Poppins + the
// CSC palette). Pure presentational; all numbers come from the `analysis` prop.
import React, { useState, useMemo } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PP = "'Poppins', sans-serif";
const COLORS = { text: '#0F172A', sub: '#64748B', border: '#E2E8F0', bg: '#F8FAFC' };

// Series colours — matched to the reference HTML.
const C = {
    day: '#2f9be0',      // TNB day import (8am–6pm)
    night: '#6b4ee6',    // TNB night import (6pm–8am)
    solar: '#f5a623',    // solar generated
    export: '#7cb342',   // export to grid
    battCharge: '#db2777',   // solar → battery (stored) — rose
    battDischarge: '#f9a8d4', // battery → load (discharged) — light pink
};

const fmt = (n) => (n == null ? '—' : Math.round(n).toLocaleString());
// Median over NON-ZERO entries → the typical ACTIVE value at this granularity
// (so night/off hours of zeros don't drag an hourly median down to ~0).
const medianNonZero = (arr) => {
    const a = arr.filter((x) => x > 0).sort((x, y) => x - y);
    if (!a.length) return 0;
    const m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

function StatCard({ label, value, unit, sub, color }) {
    return (
        <Box sx={{ flex: '1 1 160px', minWidth: 150, bgcolor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '12px', p: 1.75 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub, fontFamily: PP, mb: 0.5 }}>{label}</Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color, fontFamily: PP, lineHeight: 1.1 }}>
                {value}<span style={{ fontSize: '0.85rem', fontWeight: 600, marginLeft: 4 }}>{unit}</span>
            </Typography>
            {sub && <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP, mt: 0.4 }}>{sub}</Typography>}
        </Box>
    );
}

export default function CscLoadProfileChart({ analysis, title, subtitle, peakFrac = null }) {
    const [view, setView] = useState('daily');
    const data = useMemo(() => (analysis?.charts?.[view] || []), [analysis, view]);

    // Stat cards recompute for the current view — a typical (active) day/hour/month.
    const cards = useMemo(() => ({
        // "Solar generated" card = TOTAL generation (solarGen); the orange candle itself is
        // only the self-used portion (solarGen falls back to solar for the real upload).
        solar: medianNonZero(data.map((d) => (d.solarGen != null ? d.solarGen : d.solar))),
        day: medianNonZero(data.map((d) => d.dayImport)),
        night: medianNonZero(data.map((d) => d.nightImport)),
        exp: medianNonZero(data.map((d) => d.export)),
        // The ORANGE candle — solar the site used itself. Separate from the "Solar
        // generated" card above, which is total output including what was exported.
        selfUsed: medianNonZero(data.map((d) => d.solar)),
    }), [data]);

    /* WHICH HALF OF THE BILL the self-used solar came off. The bars above are bucketed by
       OPERATING hours (8am–6pm), which is a different thing from the TARIFF windows: ToU
       peak runs 14:00–22:00 on weekdays only, at 31.32 sen against 27.23. So a kWh in the
       blue bar may be peak or off-peak, and the bars alone cannot say which.

       `peakFrac` comes from the engine, worked out from this site's own hours and days —
       not a constant — so a site closing at 17:00 is not credited for the 17:00–20:00
       output it exports. Absent, the row is simply not drawn rather than guessed at.
       (user 2026-10-31: "which kwh is off peak which is peak? cos both rate is diff") */
    const split = useMemo(() => {
        if (peakFrac == null || !(cards.selfUsed > 0)) return null;
        const f = Math.max(0, Math.min(1, peakFrac));
        return { peak: cards.selfUsed * f, off: cards.selfUsed * (1 - f), pct: f * 100 };
    }, [peakFrac, cards.selfUsed]);

    if (!analysis || !analysis.charts) return null;
    const { flags = [] } = analysis;
    const per = view === 'monthly' ? 'per month' : view === 'hourly' ? 'per hour' : 'per day';
    const grain = view === 'monthly' ? 'month' : view === 'hourly' ? 'hour' : 'day';
    // SELCo curtails the surplus (shown as kWh but NOT credited); ATAP exports it at RM0.20.
    const isSelco = analysis.mode === 'selco';
    const exportLabel = isSelco ? 'Curtailed (not counted)' : 'Export to grid';
    const exportCardLabel = isSelco ? 'Curtailed median' : 'Export median';
    // Cap the x-axis to ~8 readable ticks regardless of series length — daily can be 28
    // synthetic days (W1·Mo…) or 30–90 real-upload days (MM-DD), hourly is 24. Combined with
    // angled labels below, the shown ticks never collide into an unreadable jumble.
    // (user 2026-07-29)
    const xInterval = view === 'monthly' ? 0 : Math.max(0, Math.ceil(data.length / 8) - 1);

    return (
        <Box sx={{ bgcolor: '#fff', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', p: { xs: 2, md: 2.5 }, fontFamily: PP }}>
            {/* Header */}
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                {title || 'Load Profile Analysis'}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: '0.75rem', color: COLORS.sub, fontFamily: PP, mb: 1.5 }}>{subtitle}</Typography>
            )}

            {/* Anomaly / mode flags */}
            {flags.length > 0 && (
                <Box sx={{ bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', p: 1.25, mb: 1.75 }}>
                    {flags.map((f, i) => (
                        <Typography key={i} sx={{ fontSize: '0.72rem', color: '#92400E', fontFamily: PP, lineHeight: 1.5 }}>
                            • {f.label}
                        </Typography>
                    ))}
                </Box>
            )}

            {/* Stat cards — typical value at the selected granularity */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
                <StatCard label="Solar generated" value={fmt(cards.solar)} unit="kWh" color={C.solar} sub={`typical ${grain}`} />
                <StatCard label="Day import median" value={fmt(cards.day)} unit="kWh" color={C.day} sub={`8am–6pm ${per}`} />
                <StatCard label="Night import median" value={fmt(cards.night)} unit="kWh" color={C.night} sub={`6pm–8am ${per}`} />
                <StatCard label={exportCardLabel} value={fmt(cards.exp)} unit="kWh" color={C.export} sub={`${isSelco ? 'curtailed' : 'to grid'} ${per}`} />
            </Box>

            {/* Which tariff line the self-used solar actually came off. */}
            {split && (
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1.5, mb: 2,
                    bgcolor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: '12px', px: 1.75, py: 1.25,
                }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub, fontFamily: PP }}>
                        Solar used on-site, against the bill
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#B45309', fontFamily: PP }}>
                        {fmt(split.peak)} kWh <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>peak @ RM0.3132</span>
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP }}>
                        {fmt(split.off)} kWh <span style={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub }}>off-peak @ RM0.2723</span>
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP }}>
                        {split.pct.toFixed(1)}% peak · ToU peak is weekdays 14:00–22:00, so weekend output is always off-peak
                    </Typography>
                </Box>
            )}

            {/* View toggle */}
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => { if (v) setView(v); }}
                sx={{ mb: 1.5, '& .MuiToggleButton-root': { fontFamily: PP, textTransform: 'capitalize', fontSize: '0.74rem', fontWeight: 600, px: 1.5, py: 0.4 } }}>
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="hourly">Hourly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>

            {/* Stacked bar chart */}
            <Box sx={{ width: '100%', height: 368 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F4" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: PP, fill: COLORS.sub }}
                            interval={xInterval} angle={-40} textAnchor="end" height={54} tickMargin={6} />
                        <YAxis tick={{ fontSize: 10, fontFamily: PP, fill: COLORS.sub }} width={44}
                            label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94A3B8', fontFamily: PP } }} />
                        <Tooltip contentStyle={{ fontFamily: PP, fontSize: '0.72rem', borderRadius: 8, border: `1px solid ${COLORS.border}` }}
                            formatter={(val, name) => [`${fmt(val)} kWh`, name]} />
                        <Legend wrapperStyle={{ fontFamily: PP, fontSize: '0.7rem' }} />
                        <Bar dataKey="dayImport" stackId="a" name="TNB day import (8am–6pm)" fill={C.day} />
                        <Bar dataKey="nightImport" stackId="a" name="TNB night import (6pm–8am)" fill={C.night} />
                        <Bar dataKey="solar" name="Solar used on-site" fill={C.solar} />
                        {analysis.hasBatt && <Bar dataKey="battCharge" name="Solar → battery" fill={C.battCharge} />}
                        {analysis.hasBatt && <Bar dataKey="battDischarge" name="Battery → load" fill={C.battDischarge} />}
                        <Bar dataKey="export" name={exportLabel} fill={isSelco ? '#94A3B8' : C.export} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
}
